import React, { useState, useEffect } from "react";
import { Card, Select, Spin, Typography, Tag, Button, Space, Divider, message } from "antd";
import { PlusOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { IAdditionalProduct, IInvoiceItem } from "../interfaces";

const { Text, Title } = Typography;

interface AdditionalProductsSelectProps {
  senderId?: string | number;
  onAddToInvoice: (item: IInvoiceItem) => void;
  disabled?: boolean;
}

export const AdditionalProductsSelect: React.FC<AdditionalProductsSelectProps> = ({
  senderId,
  onAddToInvoice,
  disabled = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<IAdditionalProduct | null>(null);

  const { data, isLoading, refetch } = useList<IAdditionalProduct>({
    resource: "additionalProducts",
    queryOptions: {
      enabled: !!senderId, // Nur laden wenn senderId vorhanden
    },
    pagination: {
      pageSize: 100,
    },
    filters: [
      ...(senderId ? [{ field: "sender_id", operator: "eq" as const, value: senderId }] : []),
      ...(searchValue ? [{ field: "search", operator: "contains" as const, value: searchValue }] : []),
    ],
  });

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setTimeout(() => refetch(), 300);
  };

  const handleAddToInvoice = (product: IAdditionalProduct) => {
    const invoiceItem: IInvoiceItem = {
      id: Date.now(),
      description: product.title + (product.description ? ` - ${product.description}` : ""),
      quantity: product.count || 1,
      price: product.price,
      tax: product.vat || 19,
      total: (product.count || 1) * product.price,
    };

    onAddToInvoice(invoiceItem);
    message.success(`${product.title} wurde als Position hinzugefügt`);
    setSelectedProduct(null);
  };

  const renderProductOption = (product: IAdditionalProduct) => (
    <div style={{ padding: "8px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <Text strong>{product.title}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {product.calculation_info}
            {product.optional && <Tag color="blue" style={{ marginLeft: 8 }}>Optional</Tag>}
            <Tag color="green" style={{ marginLeft: 4 }}>MwSt: {product.vat}%</Tag>
          </Text>
          {product.description && (
            <>
              <br />
              <Text style={{ fontSize: "12px", color: "#52c41a" }}>{product.description}</Text>
            </>
          )}
        </div>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleAddToInvoice(product);
          }}
        >
          Hinzufügen
        </Button>
      </div>
    </div>
  );

  if (!senderId) {
    return (
      <Card size="small">
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <ShoppingCartOutlined style={{ fontSize: "24px", color: "#d9d9d9" }} />
          <br />
          <Text type="secondary">Wählen Sie zuerst einen Absender aus, um Zusatzprodukte zu laden</Text>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <ShoppingCartOutlined />
          <span>Zusatzprodukte hinzufügen</span>
        </Space>
      }
      size="small"
    >
      <Select
        showSearch
        placeholder="Zusatzprodukte suchen..."
        style={{ width: "100%" }}
        onSearch={handleSearch}
        value={selectedProduct?.id}
        onChange={(value) => {
          const product = data?.data?.find((p) => p.id === value);
          setSelectedProduct(product || null);
        }}
        filterOption={false}
        loading={isLoading}
        disabled={disabled}
        allowClear
        notFoundContent={isLoading ? <Spin size="small" /> : "Keine Zusatzprodukte gefunden"}
        dropdownRender={(menu) => (
          <div>
            {data?.data && data.data.length > 0 ? (
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                {data.data.map((product) => (
                  <div key={product.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    {renderProductOption(product)}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <Text type="secondary">Keine Zusatzprodukte verfügbar</Text>
              </div>
            )}
          </div>
        )}
      />
      
      {data?.data && data.data.length > 0 && (
        <div style={{ marginTop: "12px" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {data.data.length} Zusatzprodukte verfügbar
          </Text>
        </div>
      )}
    </Card>
  );
}; 